<template>
	<Drawer ref="drawer" :title="$t('Public profile')" initialWidth="xl" @requestUnmount="hide" :closeOnClickOutside="true">

		<template #default="{ hide }">

			<form @submit.prevent="save" class="isolate">

				<div v-if="editableModel" class="isolate min-h-0 flex-1">
					<!-- Main content grid -->
					<div class="p-4 max-w-3xl mx-auto">
						<Dropzone @files-dropped="handleNewSelection">
							<div class="flex gap-4 items-center">
								<div class="relative">
									<div v-if="isUploading" class="absolute inset-0 m-auto z-10 flex items-center justify-center bg-white/50 rounded-full w-20">
										<span class="loading loading-spinner"></span>
									</div>
									<UserAvatar :user="editableModel" :usePublicProfile="true" class="h-20 w-20" />
								</div>
								<button type="button" class="flex items-center btn btn-sm border-2 border-white" @click="handleNewSelection(null)">
									<CameraIcon class="w-4 h-4" />
									<div class="text-xs text-center">{{ $t('Upload a new image') }}...</div>
								</button>
							</div>
							<Attachments
								v-model="uploads"
								:uploading="isUploading"
								@update:uploading="isUploading = $event"
								@update:modelValue="onFilesFinished"
								:preview="false"
								ref="attachmentsContainer"
								accept="image/*"
								placeholder-text=""
								class="hidden"
							/>
						</Dropzone>
						<!-- input field to change name -->
						<div class="flex flex-col gap-4 mt-6">
							<div>
								<label class="block text-sm font-medium mb-1" for="name">{{ $t('Name') }}</label>
								<input
									v-model="editableModel.publicName"
									name="publicName"
									id="name"
									type="text"
									class="input w-full"
									:class="[invalidNameError ? 'border-red-500' : '']"
									:placeholder="$t('Name')"
									autofocus
									ref="nameField"
								/>
								<p v-if="invalidNameError" class="text-xs text-red-500 mt-1">
									{{ $t('#error: field required', { fieldName: $t('Name') }) }}
								</p>
							</div>
							<div>
								<label class="block text-sm font-medium mb-1" for="description">{{ $t('Description') }}</label>
								<textarea
									v-model="editableModel.publicDescription"
									id="description"
									class="textarea textarea-bordered w-full"
									rows="6"
								></textarea>
							</div>
						</div>
					</div>
				</div>

				<div class="drawer-action-bar flex justify-end gap-1">
					<button type="button" class="btn btn-ghost" @click="hide" :disabled="isSaving">
						{{ $t("Close") }}
					</button>
					<button type="submit" class="btn btn-primary" :disabled="isSaving || isUploading">
						<span v-if="isSaving" class="loading loading-spinner"></span>
						{{ $t("Save") }}
					</button>
				</div>
			</form>
		</template>
	</Drawer>
</template>

<script lang="ts">
import { CameraIcon } from "@heroicons/vue/24/outline";
import { useUsersStore } from "@/stores/Users.store";
import Drawer from "@/components/ui/Drawer.v2.vue";
import Dropzone from "@/components/Dropzone.vue";
import Attachments from "@/components/Attachments.vue";
import { User } from "@/models/User.model";
import { mapStores } from "pinia";
import { UnsavedChanges } from "@/utils/UnsavedChanges.util";
import UserAvatar from "@/components/UserAvatar.vue";

export default {
	emits: ["requestClose"],

	components: {
		Drawer,
		CameraIcon,
		Dropzone,
		Attachments,
		UserAvatar,
	},

	data() {
		return {
			isSaving: false,
			isUploading: false,
			uploads: [] as any[],
			editableModel: null as User | null,
			errors: {} as any,
		};
	},

	computed: {
		...mapStores(useUsersStore),

		me() {
			return this.usersStore.me;
		},

		invalidNameError(): boolean {
			const name = this.editableModel?.publicName;
			return Boolean(this.errors?.fields?.publicName && (!name || !name.trim()));
		},
	},

	watch: {
        errors: {
            handler(newValue: any) {
                if (newValue?.fields?.publicName) {
                    // Focus the field after a short delay to ensure visibility
                    setTimeout(() => (this.$refs.nameField as any)?.focus(), 100);
                }
            },
            deep: true,
        },
    },

	created() {
		if (this.me) {
			this.editableModel = this.me.clone();
		}
	},

	methods: {
		hide() {
			this.$emit("requestClose");
		},

		// This handles both the button click and the drag-and-drop
		handleNewSelection(file) {
			if (file) {
				this.uploads = [];
				const files = Array.isArray(file) ? file : [file];
				this.$refs.attachmentsContainer.filesDropped(files);
			} else {
				// Reset uploads AND the internal attachmentsLocalCopy before opening dialog
				this.uploads = [];
				this.$refs.attachmentsContainer.attachmentsLocalCopy = [];
				this.$refs.attachmentsContainer.openFileDialog();
			}
		},

		onFilesFinished(allFiles) {
			if (allFiles && allFiles.length > 0) {
				const latestFile = allFiles[allFiles.length - 1];
				if (this.editableModel) {
				this.editableModel.publicImage = {
					id: latestFile.id,
					url: latestFile.url
				};
				}
				this.uploads = [latestFile];
			}
		},

		animatedClose() {
			UnsavedChanges.unobserveModel();
			this.hide();
		},

		async save() {
			if (this.isSaving) {
				return;
			}

			if (!this.editableModel?.publicName || !this.editableModel.publicName.trim()) {
                this.editableModel.publicName = "";
				this.errors = { fields: { publicName: true } };
                return;
            }

			try {
				this.isSaving = true;
				this.errors = {};

				// Apply editable changes back to the store's me object
				if (this.editableModel && this.me) {
					this.me.publicName = this.editableModel.publicName.trim();
					this.me.publicImage = this.editableModel.publicImage;
					this.me.publicDescription = this.editableModel.publicDescription;
				}

				await this.usersStore.savePreferences(this.me as User);
			}
			catch (e) {
				this.isSaving = false;
				this.$notify({ text: this.$t("An error occurred while saving your preferences. Please try again."), type: 'error' });
			}
			finally {
				this.isSaving = false;
				this.animatedClose();
			}

		},
	}
}
</script>
